/* eslint-disable import/no-duplicates */
import React, {
  useEffect,
  useRef,
  useCallback,
  useState,
  useMemo,
} from 'react';
import { useHistory } from 'react-router-dom';
import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import * as Yup from 'yup';

import { format, parseISO } from 'date-fns';

import { Container, Button, Row, Col } from 'react-bootstrap';

import { FaFileDownload } from 'react-icons/fa';
import { setTimeout } from 'timers';
import api from '../../../../services/api';

import { PERMISSAO_INDIVIDUAL, TIPO_USUARIO } from '../../../../utils/funcoes';

import { useToast } from '../../../../hooks/toast';
import { useAuth } from '../../../../hooks/auth';
import { useClinica } from '../../../../hooks/clinica';

import Input from '../../../../components/Input';
import SelectSync, { IOption } from '../../../../components/SelectSync';
import Loading from '../../../../components/Loading';

import getValidationErrors from '../../../../utils/getValidationErrors';

import { RelatorioHeader } from './styles';
import Paginacao from '../../../../components/Paginacao';
import getResponseErrors from '../../../../utils/getResponseErrors';
import { PermissionProvider } from '../../../../services/PermissionProvider';

interface IRelatorio {
  CobCodigo: number;
  CobDataAviso: Date;
  CobDataAvisoFormatada: string;
  PacNome: string;
  CprHistorico: string;
  Motivo: string;
  CobData: Date;
  CobDataFormatada: string;
  CobObservacao: string;
  CprDataVenc: Date;
  CprDataVencFormatada: string;
  Dias: string;
  UsuNome: string;
}

interface IFormBusca {
  dataInicio: Date;
  dataFim: Date;
  paciente: string;
  UsuCodigo: string;
}

export interface IPaginacao {
  termoBusca: IFormBusca;
  limite: number;
  pagina: number;
}

interface IDataExibe {
  dataHora: string;
  dataDiaSemana: string;
}

interface IUsuariosNomeCodigo {
  UsuNome: string;
  UsuCodigo: Number;
}

const CobrancasRealizados: React.FC = () => {
  const history = useHistory();
  const { user } = useAuth();

  const permissoes = useMemo(() => {
    return user.UsuPermissao.split(',');
  }, [user.UsuPermissao]);

  useEffect(() => {
    if (
      user.UsuTipo > TIPO_USUARIO.ATENDENTE_CAIXA &&
      user.UsuTipo !== TIPO_USUARIO.COBRANCA &&
      !permissoes.some(
        permissao => permissao === PERMISSAO_INDIVIDUAL.COBRANCAS_REALIZADAS,
      )
    ) {
      history.push('/sempermissao');
    }
  }, [history, user.UsuTipo]);

  const { addToast } = useToast();
  const { imagensClinica } = useClinica();

  const logo =
    imagensClinica.find(img => img.nome === 'logoRelatorio')?.arquivo_url || '';

  const formRefBusca = useRef<FormHandles>(null);
  const [loading, setLoading] = useState(false);
  const [dadosRelatorio, setDadosRelatorio] = useState<IRelatorio[]>();
  const [optionsUsuarios, setOptionsUsuarios] = useState<IOption[]>([]);
  const [usuario, setUsuario] = useState('');
  const [paginacao, setPaginacao] = useState<IPaginacao | undefined>(undefined);
  const [totalRegistros, setTotalRegistros] = useState<number | undefined>(
    undefined,
  );

  const initialData = {
    dataInicio: format(new Date(), 'yyyy-MM-dd'),
    dataFim: format(new Date(), 'yyyy-MM-dd'),
    paciente: '',
    CliCodigo: user.UsuClinica.toString(),
  };
  const [dataExibe, setDataExibe] = useState<IDataExibe>();

  useEffect(() => {
    async function loadUsuarios(): Promise<void> {
      setLoading(true);
      try {
        const response = await api.get<IUsuariosNomeCodigo[]>(
          `users/listarUsuariosChat?except_user_id=0`,
        );

        const options = response.data.map(op => ({
          value: op.UsuCodigo.toString(),
          label: op.UsuNome,
        }));

        setOptionsUsuarios(options);
      } catch (error) {
        const mensagem = getResponseErrors(error);
        addToast({
          type: 'error',
          title: 'Não foi possível carregar usuários',
          description: mensagem,
        });
      } finally {
        setLoading(false);
      }
    }
    loadUsuarios();
  }, [addToast]);

  useEffect(() => {
    async function loadCobrancas(): Promise<void> {
      if (paginacao) {
        setLoading(true);
        try {
          const dataInicioSelecionada = parseISO(
            paginacao?.termoBusca.dataInicio.toString(),
          );
          const dataFimSelecionada = parseISO(
            paginacao?.termoBusca.dataFim.toString(),
          );

          const dataInicioFormatada = format(
            dataInicioSelecionada,
            'yyyy-MM-dd',
          );
          const dataFimFormatada = format(dataFimSelecionada, 'yyyy-MM-dd');

          setDataExibe({
            dataDiaSemana: `${format(
              dataInicioSelecionada,
              'dd/MM/yyyy',
            )} até ${format(dataFimSelecionada, 'dd/MM/yyyy')}`,
            dataHora: format(new Date(), 'dd/MM/yyyy HH:mm'),
          });

          let parametrosOpcionais = paginacao?.termoBusca.UsuCodigo
            ? `&UsuCodigo=${paginacao?.termoBusca.UsuCodigo}`
            : '';

          parametrosOpcionais =
            paginacao?.termoBusca.paciente !== ''
              ? `&paciente=${paginacao?.termoBusca.paciente}`
              : '';

          const response = await api.get<{
            count: number;
            cobranca: IRelatorio[];
          }>(
            `/cobranca/?pagina=${paginacao.pagina}&limite=${paginacao.limite}&DataIni=${dataInicioFormatada}&DataFim=${dataFimFormatada}${parametrosOpcionais}`,
          );

          setTotalRegistros(
            response.data.count === 0 ? undefined : response.data.count,
          );

          const dadosFormatados = response.data.cobranca.map(rel => {
            return {
              ...rel,
              CobDataAvisoFormatada: format(
                parseISO(rel.CobDataAviso.toString()),
                'dd/MM/yyyy',
              ),
              CobDataFormatada: format(
                parseISO(rel.CobData.toString()),
                'dd/MM/yyyy',
              ),
              CprDataVencFormatada: format(
                parseISO(rel.CprDataVenc.toString()),
                'dd/MM/yyyy',
              ),
            };
          });

          setDadosRelatorio(dadosFormatados);
        } catch (error) {
          const mensagem = getResponseErrors(error);
          addToast({
            type: 'error',
            title: 'Não foi possível carregar dados de cobrança',
            description: mensagem,
          });
        } finally {
          setLoading(false);
        }
      }
    }
    loadCobrancas();
  }, [paginacao, addToast]);

  const handleSubmitBusca = useCallback(
    async (dados: IFormBusca) => {
      try {
        formRefBusca.current?.setErrors({});
        const schema = Yup.object().shape({
          dataInicio: Yup.date().required(),
          dataFim: Yup.date().required(),
          UsuCodigo: Yup.string().nullable(true),
        });

        await schema.validate(dados, {
          abortEarly: false,
        });
        if (dados?.UsuCodigo)
          setUsuario(
            optionsUsuarios.find(op => op.value === dados?.UsuCodigo)?.label ||
              '',
          );

        setPaginacao({
          termoBusca: {
            dataInicio: dados.dataInicio,
            dataFim: dados.dataFim,
            paciente: dados.paciente,
            UsuCodigo: dados?.UsuCodigo,
          },
          pagina: 1,
          limite: 50,
        });
      } catch (error) {
        if (error instanceof Yup.ValidationError) {
          const errors = getValidationErrors(error);
          formRefBusca.current?.setErrors(errors);
        }
      } finally {
        setLoading(false);
      }
    },
    [optionsUsuarios],
  );

  const handleDownload = useCallback(async () => {
    setLoading(true);
    try {
      const dados: IFormBusca = {
        dataInicio: formRefBusca.current?.getFieldValue('dataInicio'),
        dataFim: formRefBusca.current?.getFieldValue('dataFim'),
        paciente: formRefBusca.current?.getFieldValue('paciente'),
        UsuCodigo: formRefBusca.current?.getFieldValue('UsuCodigo'),
      };
      console.log(dados);
      formRefBusca.current?.setErrors({});
      const schema = Yup.object().shape({
        dataInicio: Yup.date().required(),
        dataFim: Yup.date().required(),
        UsuCodigo: Yup.string().nullable(true),
      });

      await schema.validate(dados, {
        abortEarly: false,
      });

      const dataInicioSelecionada = parseISO(dados.dataInicio.toString());
      const dataFimSelecionada = parseISO(dados.dataFim.toString());

      const dataInicioFormatada = format(dataInicioSelecionada, 'yyyy-MM-dd');
      const dataFimFormatada = format(dataFimSelecionada, 'yyyy-MM-dd');

      let parametrosOpcionais = dados.UsuCodigo
        ? `&UsuCodigo=${dados.UsuCodigo}`
        : '';

      parametrosOpcionais =
        dados.paciente !== '' ? `&paciente=${dados.paciente}` : '';

      const response = await api.get(
        `/cobranca/exportar?pagina=${1}&limite=${1}&DataIni=${dataInicioFormatada}&DataFim=${dataFimFormatada}${parametrosOpcionais}`,
      );

      const link = document.createElement('a');
      link.href = response.data.url;
      link.setAttribute(
        'download',
        `cobrancasRealizadas${Math.floor(Date.now() / 1000)}.xlsx`,
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      if (error instanceof Yup.ValidationError) {
        const errors = getValidationErrors(error);
        formRefBusca.current?.setErrors(errors);
      } else {
        const mensagem = getResponseErrors(error);
        addToast({
          type: 'error',
          title: 'Não foi possível carregar dados',
          description: mensagem,
        });
      }
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  const handleOnChange = useCallback(() => {}, []);

  const handleProximaPagina = useCallback((page: number) => {
    setPaginacao(state => {
      return state && { ...state, pagina: page };
    });
  }, []);

  return (
    <PermissionProvider
      permissions={[
        'CAIXA',
        'COBRANCA',
        PERMISSAO_INDIVIDUAL.COBRANCAS_REALIZADAS,
      ]}
    >
      <Container fluid className="mt-2">
        {loading && <Loading />}
        <>
          <div className="d-print-none bg-white border p-2 mb-2">
            <div className="p-2 mb-2">
              <h2 id="topoBusca">Cobranças Realizadas</h2>
            </div>

            <Form
              ref={formRefBusca}
              initialData={initialData}
              onSubmit={handleSubmitBusca}
            >
              <Row>
                <Col md={2}>
                  <Input name="dataInicio" label="De" type="date" required />
                </Col>
                <Col md={2}>
                  <Input name="dataFim" label="Até" type="date" required />
                </Col>

                <Col md={3}>
                  <Input name="paciente" label="Paciente" type="text" />
                </Col>

                <Col md={3}>
                  <SelectSync
                    name="UsuCodigo"
                    label="Usuário"
                    options={optionsUsuarios}
                    isClearable
                    handleSelectChange={handleOnChange}
                  />
                </Col>

                <Col md={1}>
                  <br />
                  <Button type="submit" size="sm" variant="outline-secondary">
                    BUSCAR
                  </Button>
                </Col>
                <Col md={1}>
                  <br />
                  <Button
                    type="button"
                    size="sm"
                    variant="outline-success"
                    disabled={!dadosRelatorio || dadosRelatorio.length === 0}
                    onClick={() => handleDownload()}
                  >
                    <FaFileDownload />
                  </Button>
                </Col>
              </Row>
            </Form>
          </div>

          <div className="bg-white border p-2 my-3">
            <img src={logo} alt="Agenda Facil" /> Impresso em{' '}
            {dataExibe?.dataHora}
          </div>

          <RelatorioHeader>
            COBRANÇAS REALIZADAS {`${usuario !== '' ? `- ${usuario} -` : ''}`}{' '}
            {dataExibe?.dataDiaSemana}
          </RelatorioHeader>
          <div className="bg-white border p-2">
            {dadosRelatorio && dadosRelatorio.length > 0 && (
              <>
                <table
                  className="table table-hover table-sm table-font-small table-bordered"
                  style={{ borderWidth: '0px' }}
                >
                  <thead>
                    <tr className="text-center">
                      <th style={{ width: '90px' }}>Data Cob.</th>
                      <th style={{ width: '300px' }}>Paciente</th>
                      <th style={{ width: '180px' }}>Histórico</th>
                      <th>Dias</th>
                      <th>Usuário</th>
                      <th>Motivo</th>
                      <th>Observação</th>
                      <th style={{ width: '90px' }}>Data Venc.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dadosRelatorio.map(rel => (
                      <tr
                        key={`cobranca-${rel.CobCodigo}`}
                        style={{ fontSize: '.7rem', height: '30px' }}
                      >
                        <td>{rel.CobDataFormatada}</td>
                        <td>{rel.PacNome}</td>
                        <td>{rel.CprHistorico}</td>
                        <td>{rel.Dias}</td>
                        <td>{rel.UsuNome}</td>
                        <td>{rel.Motivo}</td>
                        <td>{rel.CobObservacao}</td>
                        <td>{rel.CprDataVencFormatada}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <Paginacao
                  limitePagina={paginacao?.limite || 30}
                  paginasVizinhas={2}
                  totalRegistros={totalRegistros}
                  paginaInicio={paginacao?.pagina}
                  handleProximaPagina={handleProximaPagina}
                />
              </>
            )}
            {(!dadosRelatorio || dadosRelatorio.length === 0) && (
              <div
                className="d-flex justify-content-center align-items-center"
                style={{ minHeight: '250px' }}
              >
                <h5>Nenhum registro encontrado</h5>
              </div>
            )}
          </div>
        </>
      </Container>
    </PermissionProvider>
  );
};

export default CobrancasRealizados;
